/**
 * Генерация `/generate?pin=1&ref=5&dress=3&from=77` — песочница: референсы (пины Pinterest,
 * референсы библиотеки и вставленные картинки с комментариями), наши платья, их фото и загрузки,
 * параметры, оценка цены и запуск. Пары — каждая сцена с каждым платьем; потолок запуска —
 * КАДРАМИ (пар × n), как у сервиса. Без пинов и референсов — одна пара без сцены (`manual`).
 *
 * В адресе живут наборы пинов, референсов и платьев (их можно передать ссылкой) и
 * `from` — генерация, чьи параметры грузятся в форму («Повторить с этими параметрами»);
 * пины, референсы и платья добавляются и окнами выбора по картинкам (`RefPickerDialog`, `DressPickerDialog`);
 * после заполнения `from` уходит из адреса. Фото, параметры, «что учесть», вставленные картинки и загрузки
 * платьев — локальное состояние страницы (в адрес они не идут). Запуск платный: ключ идемпотентности живёт от ОТВЕТА до ОТВЕТА
 * (повтор после обрыва не ставит вторую задачу), замок `inFlight` держит двойной клик.
 */
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { DropZone, PicTiles, useUploader } from "@/components/fitting/PicTiles";
import { ParamsPanel } from "@/components/fitting/ParamsPanel";
import { DressPickerDialog } from "@/components/fitting/DressPickerDialog";
import type { SceneRef } from "@/lib/dressSimilar";
import { RefPickerDialog } from "@/components/fitting/RefPickerDialog";
import { RefSceneRow } from "@/components/fitting/RefSceneRow";
import { SceneCounter } from "@/components/fitting/SceneResultsSheet";
import { btn, btnGhost, btnPrimary, Empty, ErrorLine, input, label, Loading, Panel } from "@/components/trends/ui";
import {
  analyzeScene,
  buildPrompt,
  DEFAULT_MODEL,
  estimateFittings,
  isRouterModel,
  FITTINGS_PREFIX,
  fittingKeys,
  launchFittings,
  loadFittingOptions,
  loadFittings,
  loadPins,
  EXTRAS_MAX,
  MAX_IMAGES,
  MAX_INPUTS,
  saveSceneNote,
  type DressPick,
  type EstimateBody,
  type GenParams,
  type LaunchResult,
  type PinCard,
  type PinLite,
} from "@/lib/fitting";
import {
  basisLabel,
  buildBody,
  canRun as canRunOf,
  customEmpty as customEmptyOf,
  facePic,
  folderPicks,
  hasPortrait,
  ids,
  inputFlags,
  maxInputs as maxInputsOf,
  pairCount as pairCountOf,
  checkedPhotoIds,
  dressThumb,
  NO_PICS,
  sizesLabel,
  uploadedPicks,
} from "@/features/generate/model/generateForm";
import {
  customPromptOf,
  extrasOf,
  fromNoteOf,
  genFromSnapshot,
  notFoundNote,
  notRepeatable,
  uncheckedFrom,
  uploadsOf,
} from "@/features/generate/model/fromGeneration";
import { plural } from "@/lib/library";
import { mediaUrl, num } from "@/lib/media";
import { dressTitle, loadDress, loadPhotos, photoKeys, type Dress } from "@/lib/photos";
import { newKey, usd } from "@/lib/project";
import { browserStore, defaultModelCode, loadPrompt, modelParams, savePrompt } from "@/lib/promptMode";
import { promptView, takeBaseOf } from "@/lib/promptView";
import { loadReferenceCards, referenceKeys, type ReferenceLite } from "@/lib/references";
import { retryQuery } from "@/lib/trends";
import { addPics, imageFiles, type Pic } from "@/lib/uploads";

const NOTE_MAX = 500;

/** Платье из загрузки: у него нет папки, ключ `u<n>` живёт только на странице. */
type UploadedDress = { key: string; pics: Pic[] };

/** Значение после 300 мс тишины: каждая галочка не должна бить в сервис. */
function useSettled(value: string): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), 300);
    return () => clearTimeout(t);
  }, [value]);
  return settled;
}

type PromptBody = Parameters<typeof buildPrompt>[0];

export function FittingPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const pinIds = useMemo(() => ids(search.getAll("pin")), [search]);
  const refIds = useMemo(() => ids(search.getAll("ref")), [search]);
  // Референсы библиотеки в роли «внешность модели» (№318): они не сцены, а вход `portrait` каждой
  // пары. Роль живёт в адресе, иначе присланная в чат ссылка открывала бы лицо сценой.
  const faceIds = useMemo(() => ids(search.getAll("face")), [search]);
  const dressIds = useMemo(() => ids(search.getAll("dress")), [search]);
  const fromId = ids(search.getAll("from"))[0] ?? null;

  const setIds = (pins: number[], refs: number[], dresses: number[], faces: number[] = faceIds) => {
    const next = new URLSearchParams();
    for (const p of pins) next.append("pin", String(p));
    for (const r of refs) next.append("ref", String(r));
    for (const f of faces) next.append("face", String(f));
    for (const d of dresses) next.append("dress", String(d));
    if (fromId !== null) next.set("from", String(fromId));
    setSearch(next, { replace: true });
  };

  const back = (
    <button
      type="button"
      onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/trends"))}
      className="inline-flex w-fit items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg"
    >
      <ArrowLeft size={13} />
      Назад
    </button>
  );

  const pinsQuery = useQuery({
    queryKey: fittingKeys.pins(pinIds),
    queryFn: () => loadPins(pinIds),
    retry: retryQuery,
    enabled: pinIds.length > 0,
  });
  // Фильтр по текущим id обязателен: у запросов глобально включён `keepPreviousData` (дефолт
  // refine), поэтому после снятия ПОСЛЕДНЕГО пина/референса выключенный запрос продолжает отдавать
  // прежний ответ — и снятая сцена молча уходила бы в платный запуск (найдено ревью 16.10).
  const foundPins: PinLite[] = (pinsQuery.data?.pins ?? []).filter((p) => pinIds.includes(p.id));
  const foundPinIds = foundPins.map((p) => p.id);

  const refsQuery = useQuery({
    queryKey: referenceKeys.cards(refIds),
    queryFn: () => loadReferenceCards(refIds),
    retry: retryQuery,
    enabled: refIds.length > 0,
  });
  const foundRefs: ReferenceLite[] = (refsQuery.data?.items ?? []).filter((r) => refIds.includes(r.id));
  const foundRefIds = foundRefs.map((r) => r.id);

  const facesQuery = useQuery({
    queryKey: referenceKeys.cards(faceIds),
    queryFn: () => loadReferenceCards(faceIds),
    retry: retryQuery,
    enabled: faceIds.length > 0,
  });
  const foundFaces: ReferenceLite[] = (facesQuery.data?.items ?? []).filter((r) => faceIds.includes(r.id));

  const dressQueries = useQueries({
    queries: dressIds.map((id) => ({ queryKey: photoKeys.dress(id), queryFn: () => loadDress(id), retry: retryQuery })),
  });
  const dressList = useQuery({ queryKey: photoKeys.list, queryFn: loadPhotos });
  const options = useQuery({ queryKey: fittingKeys.options, queryFn: loadFittingOptions, staleTime: 60_000 });
  const opts = options.data;

  // Снятые галочки по платьям: по умолчанию отмечены все фото с view_path.
  const [unchecked, setUnchecked] = useState<Record<number, number[]>>({});
  // Параметры запуска; null — умолчания (пресет по умолчанию, весь каталог правил, модель — `defaultModelCode`).
  const [gen, setGen] = useState<GenParams | null>(null);
  // Промпт «Авто | Мой»: режим и свой текст — в браузере, переживают перезагрузку (`lib/promptMode.ts`).
  const [promptState, setPromptState] = useState(() => loadPrompt(browserStore()));
  useEffect(() => savePrompt(browserStore(), promptState), [promptState]);
  const customMode = promptState.mode === "custom";
  // «Что учесть» референсов: текст этого запуска, предзаполнен заметкой референса, в референс не пишется.
  const [refNotes, setRefNotes] = useState<Record<number, string>>({});
  const [picker, setPicker] = useState(false);
  const [refPicker, setRefPicker] = useState(false);
  const [launched, setLaunched] = useState<LaunchResult | null>(null);
  const [fromNote, setFromNote] = useState<string | null>(null);
  // Песочница: вставленные картинки, загрузки к платьям папок, платья из загрузок, «Что учесть» без сцены.
  const [extras, setExtras] = useState<Pic[]>([]);
  const [uploadsByDress, setUploadsByDress] = useState<Record<number, Pic[]>>({});
  const [uploadedDresses, setUploadedDresses] = useState<UploadedDress[]>([]);
  const [manualNote, setManualNote] = useState("");
  const nextUploaded = useRef(1);
  const newUploaded = (pics: Pic[]): UploadedDress => ({ key: `u${nextUploaded.current++}`, pics });
  // идущие загрузки всех блоков: запуск ждёт, иначе платный прогон уйдёт без ещё не загруженной картинки
  const [uploading, setUploading] = useState(0);
  const onBusy = (d: 1 | -1) => setUploading((n) => n + d);
  const [extrasCut, setExtrasCut] = useState(false);
  const extrasNow = useRef(extras);
  extrasNow.current = extras;
  const extrasUp = useUploader((items) => {
    const fresh = items.filter((u) => !extrasNow.current.some((p) => p.asset_id === u.asset_id));
    if (extrasNow.current.length + fresh.length > EXTRAS_MAX) setExtrasCut(true);
    setExtras((s) => addPics(s, items).slice(0, EXTRAS_MAX));
  }, onBusy);

  // Ctrl+V: картинки из буфера — во вставленные. В поле ввода вставка остаётся текстовой.
  const pasteUpload = useRef(extrasUp.upload);
  pasteUpload.current = extrasUp.upload;
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      const files = imageFiles(e.clipboardData?.files);
      if (files.length === 0) return;
      e.preventDefault();
      void pasteUpload.current(files);
    };
    // промахнувшийся drop не должен уводить страницу на файл
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const stop = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    document.addEventListener("paste", onPaste);
    document.addEventListener("dragover", stop);
    document.addEventListener("drop", stop);
    return () => {
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("dragover", stop);
      document.removeEventListener("drop", stop);
    };
  }, []);

  const defaults: GenParams = {
    preset: opts?.default_preset ?? "",
    quality: null,
    size: null,
    n: null,
    fit_px: opts?.fit_px_default ?? 1536,
    rules_fitting: null,
    rules_reference: null,
    rules_manual: null,
    prompt: null,
    model: null,
    aspect: null,
    resolution: null,
  };
  const defaultSpec = opts?.models.find((m) => m.code === defaultModelCode(opts.models));
  // промпт в параметрах — только «Мой»: у «Авто» его собирает сервис
  const genParams: GenParams = {
    ...(gen ?? (defaultSpec ? modelParams(defaults, defaultSpec) : defaults)),
    prompt: customMode ? promptState.text : null,
  };
  const modelSpec = opts?.models.find((m) => m.code === (genParams.model ?? DEFAULT_MODEL));
  const router = modelSpec?.provider === "openrouter";
  const nEach = genParams.n ?? (router ? 1 : (opts?.presets[genParams.preset]?.n ?? 1));
  // «что учесть» начинается пустым: заметка референса — часто данные каталога (модель, ссылка), не указание модели
  const noteOf = (r: ReferenceLite) => refNotes[r.id] ?? "";

  // Платья с хотя бы одним отмеченным фото папки или отмеченной загрузкой — они и идут в пары.
  const folderPicksList: DressPick[] = folderPicks(
    dressIds,
    dressIds.map((_, i) => dressQueries[i]?.data),
    unchecked,
    uploadsByDress,
  );
  const picks = [...folderPicksList, ...uploadedPicks(uploadedDresses)];
  // Ни пинов, ни референсов в адресе — запуск без сцены: одна пара на платье, «Что учесть» общее.
  const manualMode = pinIds.length + refIds.length === 0;
  // Лица библиотеки идут теми же входами, что вставленные портреты: ниже «входы запуска» — это
  // `allExtras`, а `extras` остаётся списком ПЛИТОК, которыми сотрудник управляет руками. Лица
  // ВПЕРЕДИ: тот же файл, вставленный ещё и картинкой, сервис схлопнет по ассету и возьмёт первую
  // роль — пусть это будет явная «внешность модели», а не подсказка.
  const faceExtras = foundFaces.map(facePic);
  const allExtras: Pic[] = [...faceExtras, ...extras.filter((e) => !faceExtras.some((f) => f.asset_id === e.asset_id))];
  // Потолок вставленных картинок общий на входы: сервис держит его жёстко (422 на `extras`), и лица
  // его не обходят — иначе одиннадцатое лицо роняло бы запуск невнятной ошибкой.
  const extrasOver = allExtras.length > EXTRAS_MAX;
  const body: EstimateBody = buildBody({
    pinIds: foundPinIds,
    refs: foundRefs,
    refNotes,
    extras: allExtras,
    manualMode,
    manualNote,
    picks,
    params: genParams,
  });
  const bodyJson = JSON.stringify(body);
  const pairCount = pairCountOf(manualMode, foundPinIds.length + foundRefIds.length, picks.length);
  // входов в самой тяжёлой паре: сцена + вставленные + фото платья
  const maxInputs = maxInputsOf(picks, allExtras.length, manualMode);
  // у модели свой потолок входов (OpenRouter — из discovery): до запуска, чтобы не ловить 400 на оценке
  const { overService, overModel, modelOff, tooManyInputs } = inputFlags(maxInputs, MAX_INPUTS, modelSpec);
  const images = pairCount * nEach;
  const tooMany = images > MAX_IMAGES;

  const settled = useSettled(bodyJson);
  const fresh = settled === bodyJson;
  const estimate = useQuery({
    queryKey: ["fitting-estimate", settled],
    queryFn: () => estimateFittings(JSON.parse(settled) as EstimateBody),
    enabled: pairCount > 0 && fresh && genParams.preset !== "" && !tooMany && !tooManyInputs,
    retry: false,
  });
  const est = pairCount > 0 && fresh && !tooMany && !tooManyInputs ? estimate.data : undefined;

  // Предпросмотр промпта ПЕРВОЙ пары — в том же порядке, что у сервиса: сначала пины, потом референсы.
  // У пина заметка — сохранённая `scene_note`: именно она уйдёт в запуск.
  const firstPin = foundPins[0];
  const firstRef = foundRefs[0];
  // products — по записи на каждое фото первой пары: фото папки — null, загрузки — их комментарий
  const first = picks[0];
  const products: (string | null)[] = first
    ? [...first.photo_ids.map(() => null), ...first.uploads.map((u) => u.comment.trim() || null)]
    : [null];
  // Роль уходит и в предпросмотр: без неё промпт на экране обещал бы «новую женщину» там, где
  // запуск потребует лицо с портрета. Без роли (подсказка) тело остаётся прежним — строкой.
  const extraNotes = allExtras.map((e) => (e.role === "portrait" ? { comment: e.comment, role: e.role } : e.comment));
  // Та же внешность модели, что видит сервис: по ней панель гасит галочки снятых правил (№317).
  const portrait = hasPortrait(allExtras);
  const mine = customMode && promptState.text.trim() !== "" ? { prompt: promptState.text } : {};
  const promptBody: PromptBody | null = firstPin
    ? { pin: firstPin.id, note: firstPin.scene_note ?? "", products, extras: extraNotes, rules: genParams.rules_fitting, ...mine }
    : firstRef
      ? { ref: firstRef.id, note: noteOf(firstRef), products, extras: extraNotes, rules: genParams.rules_reference, ...mine }
      : manualMode && first
        ? { note: manualNote.trim(), products, extras: extraNotes, rules: genParams.rules_manual, ...mine }
        : null;
  const promptJson = promptBody ? JSON.stringify(promptBody) : "";
  const promptSettled = useSettled(promptJson);
  const promptQuery = useQuery({
    queryKey: ["fitting-prompt", promptSettled],
    queryFn: () => buildPrompt(JSON.parse(promptSettled) as PromptBody),
    enabled: promptSettled !== "",
    retry: false,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const preview = promptJson !== "" ? (promptQuery.data?.prompt ?? null) : null;
  const promptTail = promptJson !== "" && customMode ? (promptQuery.data?.tail || null) : null;
  // заготовка «Моего» от любой пары страницы; без пары — нет
  const sceneCount = foundPins.length + foundRefs.length;
  const promptBase = promptJson !== "" && promptQuery.data && !promptQuery.isPlaceholderData ? takeBaseOf(promptQuery.data, sceneCount) : null;
  // разбор сцены первой пары: у пина и без пары его нет
  const scene = promptJson !== "" ? (promptQuery.data?.scene ?? null) : null;
  const sceneEstimate = promptJson !== "" ? (promptQuery.data?.scene_estimate ?? null) : null;
  // ошибка — только по текущему телу: прошлая не мигает, пока идёт дебаунс
  const previewFailed = promptJson !== "" && promptSettled === promptJson && promptQuery.isError;
  const previewView = promptView({
    hasPair: promptJson !== "",
    inputsLoading:
      (pinIds.length > 0 && pinsQuery.isPending) ||
      (refIds.length > 0 && refsQuery.isPending) ||
      (faceIds.length > 0 && facesQuery.isPending) ||
      dressQueries.some((q) => q.isPending),
    hasText: preview !== null,
    failed: previewFailed,
  });
  // «Мой» без текста: сервис молча собрал бы авто — не запускаем, пусть выберут явно
  const customEmpty = customEmptyOf(customMode, promptState.text);

  // «Повторить с этими параметрами»: первая строка ленты по генерации → форма, затем `from` уходит из адреса.
  const fromQuery = useQuery({
    queryKey: fittingKeys.feed({ generation: fromId ?? 0 }),
    queryFn: () => loadFittings({ generation: fromId ?? 0 }),
    enabled: fromId !== null,
    retry: retryQuery,
  });
  const fromRow = fromQuery.data?.gens[0];
  const fromDressAt = fromRow?.own_dress_id != null ? dressIds.indexOf(fromRow.own_dress_id) : -1;
  const fromDress = fromDressAt >= 0 ? dressQueries[fromDressAt] : undefined;
  const fromReady =
    fromId !== null && (fromQuery.isError || (fromQuery.isSuccess && opts !== undefined && !fromDress?.isPending));
  const filled = useRef<number | null>(null);
  useEffect(() => {
    if (!fromReady || fromId === null || filled.current === fromId) return;
    filled.current = fromId;
    const g = fromQuery.data?.gens[0];
    if (!g || notRepeatable(g, opts) || !opts) {
      setFromNote(notFoundNote(fromId));
    } else {
      const p = g.params!;
      const { gen, lost, wanted } = genFromSnapshot(g, opts, isRouterModel);
      setGen(gen);
      // промпт повтора: «Мой» этого запуска — в своё поле (оно и сохранится), авто — переключение на «Авто»
      const mineText = customPromptOf(g);
      setPromptState((s) =>
        mineText ? { mode: "custom", text: mineText, ref: g.reference_item_id ?? null } : { ...s, mode: "auto" },
      );
      const folder = fromDress?.data;
      const keep = p.photo_ids;
      if (g.own_dress_id !== null && folder && keep) {
        const dressId = g.own_dress_id;
        setUnchecked((s) => ({ ...s, [dressId]: uncheckedFrom(folder.photos.map((ph) => ph.id), keep) }));
      }
      if (g.kind === "reference" && g.reference_item_id !== null) {
        const refId = g.reference_item_id;
        setRefNotes((s) => ({ ...s, [refId]: g.note ?? "" }));
      }
      // песочница: вставленные картинки, «Что учесть» и загрузки платья (пути — во входах строки по asset_id)
      setExtras(extrasOf(g));
      if (g.kind === "manual") setManualNote(g.note ?? "");
      const ups: Pic[] = uploadsOf(g);
      if (g.dress?.uploaded) {
        if (ups.length) setUploadedDresses((s) => [...s, newUploaded(ups)]);
      } else if (g.own_dress_id !== null && ups.length) {
        const dressId = g.own_dress_id;
        setUploadsByDress((s) => ({ ...s, [dressId]: ups }));
      }
      const lostLabel = lost ? (opts.models.find((m) => m.code === wanted)?.label ?? wanted) : null;
      setFromNote(fromNoteOf(g, fromId, lostLabel));
    }
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("from");
        return next;
      },
      { replace: true },
    );
  }, [fromReady, fromId, fromQuery.data, fromDress?.data, opts, setSearch]);

  const key = useRef(newKey());
  const inFlight = useRef(false);
  const queryClient = useQueryClient();
  // Пины, у которых заметка сцены ещё сохраняется: запуск ждёт, иначе уйдёт старый текст.
  const [noteSaving, setNoteSaving] = useState(0);
  const markSaving = (on: boolean) => setNoteSaving((n) => n + (on ? 1 : -1));
  const launch = useMutation({
    mutationFn: () => launchFittings({ ...body, key: key.current }),
    onSuccess: (got) => {
      key.current = newKey();
      setLaunched(got);
      // обновляет счётчики генераций у сцен (и ленту результатов на других страницах);
      // строки референсов — ради «уже было с #N»
      void queryClient.invalidateQueries({ queryKey: FITTINGS_PREFIX });
      void queryClient.invalidateQueries({ queryKey: ["references"] });
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const analyze = useMutation({
    mutationFn: (ref: number) => analyzeScene(ref),
    // onSettled и с возвратом промиса: isPending держится до пересборки предпросмотра, в том числе после ошибки
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["fitting-prompt"] }),
  });
  const run = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    launch.mutate();
  };

  const scenesLoaded = (pinIds.length === 0 || pinsQuery.isSuccess) && (refIds.length === 0 || refsQuery.isSuccess);
  if (!manualMode && scenesLoaded && foundPinIds.length + foundRefIds.length === 0)
    return (
      <div className="mx-auto grid max-w-[1000px] gap-3 p-5">
        {back}
        <Empty>
          Выбранные сцены не найдены
          <div className="mt-2 flex justify-center gap-4">
            <Link to="/generate" className="text-accent-text hover:underline">
              Пустая песочница
            </Link>
            <Link to="/trends" className="text-accent-text hover:underline">
              К тематикам
            </Link>
            <Link to="/lib" className="text-accent-text hover:underline">
              Библиотеки
            </Link>
          </div>
        </Empty>
      </div>
    );

  // Платья, с которыми уже генерировали по референсам страницы: dress → id референсов.
  const tried = new Map<number, number[]>();
  for (const r of foundRefs) for (const d of r.generated_dress_ids) tried.set(d, [...(tried.get(d) ?? []), r.id]);
  const annotate = new Map([...tried].map(([d, refs]) => [d, `уже было с ${refs.map((id) => `#${id}`).join(", ")}`]));
  // фишка окна выбора прячет платья, с которыми было со ВСЕМИ референсами страницы
  const triedAll =
    foundRefs.length > 0
      ? new Set([...tried].filter(([, refs]) => refs.length === foundRefs.length).map(([d]) => d))
      : null;
  // сцены страницы в порядке строк (пины, затем референсы) — для «Похожие на сцену» в окне платьев
  const scenes: SceneRef[] = [
    ...foundPins.map((p): SceneRef => ({ kind: "pin", id: p.id, thumb: p.thumb_path ?? p.asset_path, title: p.title ?? p.code })),
    ...foundRefs.map(
      (r): SceneRef => ({ kind: "ref", id: r.id, thumb: r.asset_path, title: `${r.library_name} · ${r.category_name}` }),
    ),
  ];
  const addNew = (have: number[], more: number[]) => [...have, ...more.filter((x) => !have.includes(x))];

  const canRun = !extrasOver && canRunOf({
    pairCount,
    tooMany,
    tooManyInputs,
    uploading,
    launching: launch.isPending,
    noteSaving,
    hasEstimate: est !== undefined,
    fromId,
    customEmpty,
  });
  const basis = basisLabel(est);
  const sizes = sizesLabel(est);
  const estModels = est
    ? [...new Set(est.pairs.map((p) => opts?.models.find((m) => m.code === p.model)?.label ?? p.model))].join(", ")
    : "";
  const estimateLine =
    pairCount === 0 ? (
      "Нет пар: нужно платье с отмеченным фото или загрузкой."
    ) : modelOff && modelSpec ? (
      `${modelSpec.label} недоступна: ${modelOff}.`
    ) : tooManyInputs ? (
      "Оценка появится, когда входов станет не больше предела."
    ) : tooMany ? (
      `${pairCount} ${plural(pairCount, "пара", "пары", "пар")} · ${images} ${plural(images, "кадр", "кадра", "кадров")}`
    ) : est ? (
      <>
        {est.pairs.length} {plural(est.pairs.length, "пара", "пары", "пар")} · {est.images}{" "}
        {plural(est.images, "кадр", "кадра", "кадров")} · ≈ {usd(est.total, 2)}
        {estModels ? <span className="text-fg-faint"> · {estModels}</span> : null}
        {basis ? <span className="text-fg-faint"> · {basis}</span> : null}
        {sizes ? <span className="text-fg-faint"> · {router ? "пропорция" : "размер"} {sizes}</span> : null}
        <span className="text-fg-faint"> · входов в паре: {maxInputs}</span>
      </>
    ) : estimate.isError && fresh ? (
      <ErrorLine error={estimate.error} />
    ) : (
      "считаю…"
    );
  const warning = (
    <>
      {tooMany ? (
        <div className="text-[12px] text-err">
          Кадров {images}, за один запуск — не больше {MAX_IMAGES}
        </div>
      ) : null}
      {extrasOver ? (
        <div className="text-[12px] text-err">
          Картинок и лиц во входах {allExtras.length}, за один запуск — не больше {EXTRAS_MAX}: уберите лишние.
        </div>
      ) : null}
      {overService ? (
        <div className="text-[12px] text-err">
          Входов в паре {maxInputs}, не больше {MAX_INPUTS}: уберите вставленные картинки или фото платья.
        </div>
      ) : null}
      {overModel && modelSpec ? (
        <div className="text-[12px] text-err">
          у {modelSpec.label} не больше {modelSpec.max_inputs} {modelSpec.max_inputs === 1 ? "картинки" : "картинок"}, в паре {maxInputs}: уберите
          вставленные картинки или фото платья, либо выберите другую модель.
        </div>
      ) : null}
      {modelOff && modelSpec ? (
        <div className="text-[12px] text-err">
          {modelSpec.label} недоступна: {modelOff}.
        </div>
      ) : null}
      {nEach > 2 || genParams.fit_px === 2048 ? (
        <div className="text-[12px] text-warn">входные картинки списываются за каждый кадр</div>
      ) : null}
      {customEmpty ? (
        <div className="text-[12px] text-err">Промпт «Мой» пуст: впишите свой текст или переключитесь на «Авто».</div>
      ) : null}
    </>
  );

  const started = launched?.generations.filter((g) => !g.existing).length ?? 0;

  return (
    <div className="mx-auto grid max-w-[1000px] gap-4 p-5" data-annot="генерация">
      <div className="flex flex-wrap items-center gap-3">
        {back}
        <h1 className="text-[16px] font-semibold text-fg">Генерация</h1>
      </div>
      {fromNote ? <div className="text-[12.5px] text-warn">{fromNote}</div> : null}

      <Panel
        title="Платья"
        annot="платья"
        aside={
          <>
            <button type="button" className={btn} onClick={() => setPicker(true)} title="Папки «Наших фото» плиткой">
              <Plus size={12} />
              Добавить платье
            </button>
            <button
              type="button"
              className={btn}
              title="Платье без папки: загрузите его фото"
              onClick={() => setUploadedDresses((s) => [...s, newUploaded([])])}
            >
              Платье из загрузки
            </button>
          </>
        }
      >
        {dressIds.length === 0 && uploadedDresses.length === 0 ? (
          <Empty>Добавьте платье из папки или загрузите его фото.</Empty>
        ) : (
          <div className="grid gap-3 p-3">
            {dressIds.map((id, i) => {
              const q = dressQueries[i];
              return (
                <DressBlock
                  key={id}
                  id={id}
                  dress={q?.data}
                  loading={q?.isPending ?? true}
                  error={q?.error}
                  badge={annotate.get(id) ?? null}
                  off={unchecked[id] ?? []}
                  uploads={uploadsByDress[id] ?? NO_PICS}
                  onUploads={(next) => setUploadsByDress((s) => ({ ...s, [id]: next }))}
                  onAdd={(items) => setUploadsByDress((s) => ({ ...s, [id]: addPics(s[id] ?? NO_PICS, items) }))}
                  onBusy={onBusy}
                  onToggle={(photoId) =>
                    setUnchecked((s) => {
                      const cur = s[id] ?? [];
                      return { ...s, [id]: cur.includes(photoId) ? cur.filter((x) => x !== photoId) : [...cur, photoId] };
                    })
                  }
                  onRemove={() =>
                    setIds(
                      pinIds,
                      refIds,
                      dressIds.filter((x) => x !== id),
                    )
                  }
                />
              );
            })}
            {uploadedDresses.map((d, i) => (
              <UploadedDressBlock
                key={d.key}
                title={`Платье из загрузки ${i + 1}`}
                pics={d.pics}
                onPics={(next) => setUploadedDresses((s) => s.map((x) => (x.key === d.key ? { ...x, pics: next } : x)))}
                onAdd={(items) =>
                  setUploadedDresses((s) => s.map((x) => (x.key === d.key ? { ...x, pics: addPics(x.pics, items) } : x)))
                }
                onBusy={onBusy}
                onRemove={() => setUploadedDresses((s) => s.filter((x) => x.key !== d.key))}
              />
            ))}
          </div>
        )}
      </Panel>

      <DropZone onFiles={(files) => void extrasUp.upload(files)}>
      <Panel
        title="Референсы"
        annot="референсы"
        aside={
          <button type="button" className={btn} onClick={() => setRefPicker(true)} title="Пины подборок и референсы библиотек плиткой">
            <Plus size={12} />
            Из пинов и библиотек
          </button>
        }
      >
        <div className="grid gap-2 p-3">
          {pinIds.length > 0 && pinsQuery.isPending ? <Loading /> : null}
          <ErrorLine error={pinsQuery.error} />
          {pinsQuery.isSuccess
            ? pinIds.map((id) => {
                const pin = foundPins.find((p) => p.id === id);
                const remove = () =>
                  setIds(
                    pinIds.filter((x) => x !== id),
                    refIds,
                    dressIds,
                  );
                return pin ? (
                  <SceneRow key={`pin-${id}`} pin={pin} onSaving={markSaving} onRemove={remove} />
                ) : (
                  <Missing key={`pin-${id}`} text={`пин #${id} не найден`} onRemove={remove} />
                );
              })
            : null}
          {refIds.length > 0 && refsQuery.isPending ? <Loading /> : null}
          <ErrorLine error={refsQuery.error} />
          {refsQuery.isSuccess
            ? refIds.map((id) => {
                const item = foundRefs.find((r) => r.id === id);
                const remove = () =>
                  setIds(
                    pinIds,
                    refIds.filter((x) => x !== id),
                    dressIds,
                  );
                return item ? (
                  <RefSceneRow
                    key={`ref-${id}`}
                    item={item}
                    note={noteOf(item)}
                    onNote={(text) => setRefNotes((s) => ({ ...s, [id]: text }))}
                    onRemove={remove}
                    role="scene"
                    // роль переносит id между `ref` и `face`: лицо перестаёт быть сценой, и наоборот
                    onRole={(next) =>
                      next === "face"
                        ? setIds(pinIds, refIds.filter((x) => x !== id), dressIds, [...faceIds, id])
                        : undefined
                    }
                  />
                ) : (
                  <Missing key={`ref-${id}`} text={`референс #${id} не найден`} onRemove={remove} />
                );
              })
            : null}
          {faceIds.length > 0 && facesQuery.isPending ? <Loading /> : null}
          <ErrorLine error={facesQuery.error} />
          {facesQuery.isSuccess
            ? faceIds.map((id) => {
                const item = foundFaces.find((r) => r.id === id);
                const remove = () => setIds(pinIds, refIds, dressIds, faceIds.filter((x) => x !== id));
                return item ? (
                  <RefSceneRow
                    key={`face-${id}`}
                    item={item}
                    note=""
                    onNote={() => undefined}
                    onRemove={remove}
                    role="face"
                    onRole={(next) =>
                      next === "scene"
                        ? setIds(pinIds, [...refIds, id], dressIds, faceIds.filter((x) => x !== id))
                        : undefined
                    }
                  />
                ) : (
                  <Missing key={`face-${id}`} text={`референс #${id} не найден`} onRemove={remove} />
                );
              })
            : null}
          <PicTiles
            pics={extras}
            onChange={(next) => {
              setExtrasCut(false);
              setExtras(next);
            }}
            roles
            altPrefix="вставленная картинка"
            onFiles={(files) => void extrasUp.upload(files)}
            hint={`вставьте Ctrl+V или перетащите сюда (до ${EXTRAS_MAX})`}
          />
          {extrasUp.status}
          {extrasCut ? <div className="text-[12px] text-warn">не больше {EXTRAS_MAX} картинок, лишние не добавлены</div> : null}
          {manualMode ? (
            <label className="grid gap-1">
              <span className={label}>Что учесть (общее для запуска без сцены)</span>
              <textarea
                className={`${input} min-h-[56px] w-full resize-y py-1`}
                placeholder="что важно: место, свет, поза, настроение"
                value={manualNote}
                maxLength={opts?.note_max ?? 1000}
                onChange={(e) => setManualNote(e.target.value)}
              />
            </label>
          ) : null}
        </div>
      </Panel>
      </DropZone>

      <Panel title="Параметры" annot="параметры">
        <div className="grid gap-3 p-3">
          {options.isPending ? (
            <Loading />
          ) : options.isError ? (
            <ErrorLine error={options.error} />
          ) : (
            <ParamsPanel
              options={options.data}
              value={genParams}
              onChange={setGen}
              kinds={{ pins: foundPinIds.length > 0, refs: foundRefIds.length > 0, manual: manualMode }}
              portrait={portrait}
              preview={preview}
              previewView={previewView}
              previewError={previewFailed ? promptQuery.error : null}
              promptMode={promptState.mode}
              onPromptMode={(mode) => setPromptState((s) => ({ ...s, mode }))}
              customPrompt={promptState.text}
              onCustomPrompt={(text) => setPromptState((s) => ({ ...s, text }))}
              onTakeBase={(text) =>
                setPromptState((s) => ({ ...s, text, ref: sceneCount === 1 ? (firstRef?.id ?? null) : null }))
              }
              customRef={promptState.ref}
              firstRefId={firstRef?.id ?? null}
              tail={promptTail}
              base={promptBase}
              scene={scene}
              sceneEstimate={sceneEstimate}
              sceneCount={sceneCount}
              onAnalyzeScene={() => {
                if (firstRef) analyze.mutate(firstRef.id);
              }}
              analyzing={analyze.isPending}
              analyzeError={firstRef && analyze.variables === firstRef.id ? analyze.error : null}
              estimateLine={estimateLine}
              warning={warning}
            />
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className={btnPrimary} disabled={!canRun} onClick={run}>
              {est ? `Сгенерировать ${est.pairs.length} за ≈ ${usd(est.total, 2)}` : "Сгенерировать"}
            </button>
            {uploading > 0 ? <span className="text-[12px] text-warn">загрузка картинок… запуск подождёт</span> : null}
          </div>
          <ErrorLine error={launch.error} />
          {launched && launched.errors.length ? (
            <ul className="grid gap-1 rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
              {launched.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          ) : null}
          {started > 0 ? (
            <div className="text-[12.5px] text-fg-muted">
              Запущено {started} {plural(started, "генерация", "генерации", "генераций")} ·{" "}
              <a href="/results" target="_blank" rel="noopener" className="text-accent-text hover:underline">
                Результаты ↗
              </a>
            </div>
          ) : null}
        </div>
      </Panel>

      <RefPickerDialog
        open={refPicker}
        onClose={() => setRefPicker(false)}
        takenPins={pinIds}
        takenRefs={[...refIds, ...faceIds]}
        dresses={dressIds.map((id, i) => {
          const d = dressQueries[i]?.data;
          return {
            id,
            title: d ? dressTitle(d) : `Платье #${id}`,
            photoIds: d ? checkedPhotoIds(d, unchecked[id] ?? []) : [],
            thumbPath: d ? dressThumb(d, unchecked[id] ?? []) : null,
          };
        })}
        onAdd={(pins, refs) => setIds(addNew(pinIds, pins), addNew(refIds, refs), dressIds)}
      />
      <DressPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        list={dressList.data}
        taken={dressIds}
        annotate={annotate}
        triedAll={triedAll}
        scenes={scenes}
        onAdd={(ids) => setIds(pinIds, refIds, addNew(dressIds, ids))}
      />
    </div>
  );
}

function Missing({ text, onRemove }: { text: string; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-2 text-[12.5px] text-err">
      {text}
      <button type="button" className={btnGhost} onClick={onRemove}>
        убрать
      </button>
    </div>
  );
}

function SceneRow({
  pin,
  onRemove,
  onSaving,
}: {
  pin: PinLite;
  onRemove: () => void;
  onSaving: (on: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const saved = pin.scene_note ?? "";
  const [text, setText] = useState(saved);
  useEffect(() => setText(saved), [saved]);
  const save = useMutation({
    mutationFn: (value: string) => saveSceneNote(pin.id, value),
    onMutate: () => onSaving(true),
    onSettled: () => onSaving(false),
    // кэш пина и списка пинов страницы — свежей заметкой, чтобы старое значение не затёрло новое
    onSuccess: (got) => {
      queryClient.setQueryData<PinCard>(fittingKeys.pin(pin.id), (old) =>
        old ? { ...old, scene_note: got.scene_note } : old,
      );
      queryClient.setQueriesData<{ pins: PinLite[] }>({ queryKey: ["pins"] }, (old) =>
        old && Array.isArray(old.pins)
          ? { ...old, pins: old.pins.map((p) => (p.id === pin.id ? { ...p, scene_note: got.scene_note } : p)) }
          : old,
      );
    },
  });
  const commit = () => {
    if (text.trim() === saved.trim() || save.isPending) return;
    save.mutate(text.trim());
  };
  const picture = pin.thumb_path ?? pin.asset_path;

  return (
    <div className="flex gap-3 rounded-[var(--radius-ui)] border border-border p-2">
      {picture ? (
        <img src={mediaUrl(picture)} alt="" className="h-24 w-16 flex-none rounded-[4px] object-cover" />
      ) : (
        <div className="grid h-24 w-16 flex-none place-items-center rounded-[4px] bg-bg-elevated text-[11px] text-fg-faint">
          нет
        </div>
      )}
      <div className="grid min-w-0 flex-1 gap-1 text-[12.5px]">
        <div className="flex items-center gap-2">
          <Link to={`/pins/${pin.id}`} className="min-w-0 truncate font-medium text-accent-text hover:underline">
            {pin.title || `Пин ${pin.code}`}
          </Link>
          <SceneCounter scene={{ kind: "pin", id: pin.id, thumb: picture, title: pin.title ?? pin.code }} />
          <span className="flex-none text-[11.5px] text-fg-faint">сохранений: {num(pin.saves)}</span>
          <button type="button" title="убрать пин" onClick={onRemove} className={`${btnGhost} ml-auto`}>
            <X size={13} />
            убрать
          </button>
        </div>
        {!pin.asset_path ? <div className={label}>оригинал скачается при запуске</div> : null}
        {pin.kind === "video" ? <div className={label}>видео: в генерацию идёт обложка</div> : null}
        <textarea
          className={`${input} min-h-[48px] w-full resize-y py-1`}
          placeholder="что важно в сцене"
          value={text}
          maxLength={NOTE_MAX}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
        />
        <ErrorLine error={save.error} />
      </div>
    </div>
  );
}

function DressBlock({
  id,
  dress,
  loading,
  error,
  badge,
  off,
  uploads,
  onUploads,
  onAdd,
  onBusy,
  onToggle,
  onRemove,
}: {
  id: number;
  dress: Dress | undefined;
  loading: boolean;
  error: unknown;
  /** «уже было с #5» — с этим платьем по референсам страницы уже генерировали. */
  badge: string | null;
  off: number[];
  /** Загрузки к этому платью: идут в запуск вместе с фото папки (отмеченные). */
  uploads: Pic[];
  onUploads: (next: Pic[]) => void;
  /** Дозагрузка: функциональное добавление в состояние страницы (не по снимку `uploads`). */
  onAdd: (items: Pic[]) => void;
  onBusy: (delta: 1 | -1) => void;
  onToggle: (photoId: number) => void;
  onRemove: () => void;
}) {
  const title = dress ? dressTitle(dress) : `Платье #${id}`;
  const usable =
    (dress ? checkedPhotoIds(dress, off).length : 0) +
    uploads.filter((p) => p.on).length;
  const up = useUploader(onAdd, onBusy);

  return (
    <DropZone onFiles={(files) => void up.upload(files)}>
    <div className="grid gap-1.5" data-annot="платье">
      <div className="flex items-center gap-2 text-[12.5px]">
        <span className="font-medium text-fg">{title}</span>
        {badge ? <span className="text-[11.5px] text-warn">{badge}</span> : null}
        <button type="button" title="убрать платье" onClick={onRemove} className={`${btnGhost} ml-auto`}>
          <X size={13} />
          убрать платье
        </button>
      </div>
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorLine error={error} />
      ) : dress ? (
        <>
          {usable === 0 ? (
            <div className="text-[12px] text-warn">Не отмечено ни одного фото — платье в пары не пойдёт.</div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {dress.photos.map((p) => {
              const broken = p.view_path === null;
              const on = !broken && !off.includes(p.id);
              return (
                <label
                  key={p.id}
                  title={broken ? (p.error ?? "конвертация не удалась") : undefined}
                  className={`relative block w-[72px] ${broken ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
                >
                  <img
                    src={mediaUrl(p.thumb_path ?? p.view_path ?? p.asset_path)}
                    alt=""
                    className={`aspect-[3/4] w-full rounded-[4px] border object-cover ${on ? "border-accent ring-1 ring-accent" : "border-border"}`}
                  />
                  <input
                    type="checkbox"
                    className="absolute top-1 left-1"
                    checked={on}
                    disabled={broken}
                    onChange={() => onToggle(p.id)}
                  />
                </label>
              );
            })}
          </div>
          <PicTiles
            pics={uploads}
            checkable
            onChange={onUploads}
            onFiles={(files) => void up.upload(files)}
            hint="свои фото этого платья: перетащите на блок платья или выберите"
            placeholder="комментарий к фото, например «вид сзади»"
            emptyHint="без комментария — стандартная подсказка о товаре"
            altPrefix="фото платья"
          />
          {up.status}
        </>
      ) : null}
    </div>
    </DropZone>
  );
}

/** Платье без папки: только загруженные фото; в запуск идёт, если отмечено хотя бы одно. */
function UploadedDressBlock({
  title,
  pics,
  onPics,
  onAdd,
  onBusy,
  onRemove,
}: {
  title: string;
  pics: Pic[];
  onPics: (next: Pic[]) => void;
  onAdd: (items: Pic[]) => void;
  onBusy: (delta: 1 | -1) => void;
  onRemove: () => void;
}) {
  const up = useUploader(onAdd, onBusy);
  const usable = pics.filter((p) => p.on).length;
  return (
    <DropZone onFiles={(files) => void up.upload(files)}>
    <div className="grid gap-1.5" data-annot="платье из загрузки">
      <div className="flex items-center gap-2 text-[12.5px]">
        <span className="font-medium text-fg">{title}</span>
        <button type="button" title="убрать платье" onClick={onRemove} className={`${btnGhost} ml-auto`}>
          <X size={13} />
          убрать платье
        </button>
      </div>
      {usable === 0 ? (
        <div className="text-[12px] text-warn">Загрузите и отметьте хотя бы одно фото — платье в пары не пойдёт.</div>
      ) : null}
      <PicTiles
        pics={pics}
        checkable
        onChange={onPics}
        onFiles={(files) => void up.upload(files)}
        hint="перетащите фото платья на блок или выберите"
        placeholder="комментарий к фото, например «вид сзади»"
        emptyHint="без комментария — стандартная подсказка о товаре"
        altPrefix="фото платья"
      />
      {up.status}
    </div>
    </DropZone>
  );
}
